import { Inject, Injectable } from '@angular/core';
import {
  EPhaseOptions,
  ITaskPerformanceData, ITaskPerformanceFilter,
} from './task-performance.model';
import { Observable, Subject } from 'rxjs';
import {
  GetManyResponseInterface,
} from '../../../shared/model/interface/crud-response-interface.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import {
  CreateExcelSheetInterface,
  ExcelColumnDefinitionInterface,
  ExcelDateFormatInformationInterface,
  ExcelHelperService,
  ExcelSheetTypeEnum,
} from '../../../shared/service/excel/excel-helper.service';
import { excelDateFormat, excelTimeFormat } from '../../../shared/model/enum/excel-date-format';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as oeeAppReducer from '../../oee.reducer';
import moment from 'moment-timezone';
import * as AppActions from '../../app/actions';
import * as ObjectActions from './task-performance.actions';
import { ITaskPerformanceTableData } from '../../../view/reports/task-performance/task-performance.model';

@Injectable({
  providedIn: 'root',
})
export class TaskPerformanceService {
  private readonly routes = {
    lines: `${this.baseUrl}/lines`,
    activityHistories: `${this.baseUrl}/activity-histories`,
    currentShifts: `${this.baseUrl}/lines/current-shifts`,
    sites: `${this.baseUrl}/sites`,
    taskPerformance: `${this.baseUrl}/tasks/task-performance`,
  };
  private readonly destroySubject: Subject<boolean> = new Subject<boolean>();
  private dateFormatInformation: ExcelDateFormatInformationInterface;

  constructor(
    @Inject('API_BASE_URL') private readonly baseUrl: string,
    public http: HttpClient,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly excelHelper: ExcelHelperService,
    private readonly translate: TranslateService,
  ) {
    let timezone: string = 'utc';
    let dateFormat$: string;
    let timeFormat$: string;

    this.store
      .select('user')
      .pipe(takeUntil(this.destroySubject))
      .subscribe((state) => {
        if (state.isUserLoaded) {
          timezone = state.timezone;

          if (state.locale !== '') {
            dateFormat$ = excelDateFormat[state.locale];
            timeFormat$ = excelTimeFormat[state.locale];
          }

          this.dateFormatInformation = {
            timezone,
            dateFormat$,
            timeFormat$,
          };

          this.destroySubject.next(true);
          this.destroySubject.complete();
        }
      });
  }

  public downloadExcel(
    worksheetsColumnDefinitions: ExcelColumnDefinitionInterface[][],
    data: ITaskPerformanceTableData[],
  ): void {
    const sheetTitle: string | any = this.translate.instant('pageTitles.task_performance_report');
    const excelName: string = `${sheetTitle} ${moment()
      .tz(this.dateFormatInformation.timezone)
      .format(this.dateFormatInformation.dateFormat$)}`;

    const worksheets: CreateExcelSheetInterface[] = [
      {
        sheetTitle: this.translate.instant('activityLogs.excel.readme.worksheetName'),
        sheetType: ExcelSheetTypeEnum.README,
      },
      {
        sheetTitle: this.translate.instant('activityLogs.excel.readme.templateFormatTitle'),
        sheetType: ExcelSheetTypeEnum.TABLE,
        params: {
          data,
          columns: worksheetsColumnDefinitions[0],
        },
        withData: true,
        isDisabledColumnsFirstLine: true,
        addDateTimeFormula: undefined,
        excelRowFormatLimit: data.length + 1,
      },
      {
        sheetTitle: this.translate.instant('activityLogs.excel.readme.dataAnalysisFormatTitle'),
        sheetType: ExcelSheetTypeEnum.TABLE,
        params: {
          data,
          columns: worksheetsColumnDefinitions[1],
        },
        withData: true,
        isDisabledColumnsFirstLine: true,
        addDateTimeFormula: undefined,
        excelRowFormatLimit: data.length + 1,
      },
    ];

    this.excelHelper
      .createExcel(
        excelName,
        { name: 'taskPerformance' },
        worksheets,
        this.dateFormatInformation.timezone,
        this.dateFormatInformation.dateFormat$,
        this.dateFormatInformation.timeFormat$,
        false,
      )
      .then(
        () => {
          this.store.dispatch(new ObjectActions.TaskPerformanceDownloadExcelCompleted());
          this.store.dispatch(new AppActions.HideLoader());
        },
        () => {
          this.store.dispatch(new ObjectActions.FetchDataError({}));
          this.store.dispatch(new AppActions.HideLoader());
        },
      );
  }

  public getTaskPerformanceData(
    params: ITaskPerformanceFilter,
  ): Observable<GetManyResponseInterface<ITaskPerformanceData>> {
    return this.http.post<GetManyResponseInterface<ITaskPerformanceData>>(`${this.routes.taskPerformance}`, params, {
      headers: new HttpHeaders({ 'X-HTTP-Method': 'GET' }),
    });
  }

  public getPhaseNames(row: ITaskPerformanceData) {
    const defaultPhaseNames = {
      [EPhaseOptions.NO_PHASE]: this.translate.instant('activityCards.activityEditForm.phaseOptions.noPhase'),
      [EPhaseOptions.PRE_RUN]: this.translate.instant('activityCards.activityEditForm.phaseOptions.pre'),
      [EPhaseOptions.RUN]: this.translate.instant('activityCards.activityEditForm.phaseOptions.run'),
      [EPhaseOptions.POST_RUN]: this.translate.instant('activityCards.activityEditForm.phaseOptions.post'),
    };

    let phaseName: string = '';

    switch (row.phaseId) {
      case EPhaseOptions.PRE_RUN:
        phaseName =
          row.sitePreRunPhaseName && row.sitePreRunPhaseName.trim() !== ''
            ? row.sitePreRunPhaseName
            : defaultPhaseNames[EPhaseOptions.PRE_RUN];
        break;
      case EPhaseOptions.POST_RUN:
        phaseName =
          row.sitePostRunPhaseName && row.sitePostRunPhaseName.trim() !== ''
            ? row.sitePostRunPhaseName
            : defaultPhaseNames[EPhaseOptions.POST_RUN];
        break;
      case EPhaseOptions.RUN:
        phaseName =
          row.siteRunPhaseName && row.siteRunPhaseName.trim() !== ''
            ? row.siteRunPhaseName
            : defaultPhaseNames[EPhaseOptions.RUN];
        break;
      default:
        phaseName = defaultPhaseNames[EPhaseOptions.NO_PHASE];
        break;
    }

    return phaseName;
  }
}
